import React from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getBlogPost } from '../../../moduls/blog'
import type { BlogBanner } from '../../../moduls/blog'
import { getReadableDate } from '../../../services/utils'
import { sanitizeHtml } from '../../../services/sanitizeHtml'
import usePageMeta from '../../../hooks/usePageMeta'
import backIcon from '../../../assets/images/front/svg/blog-back.svg'
import SkolaBannerSecondaryMobile from '../../../assets/images/front/skola-banner-secondary-mobile.png'

interface BlogPostData {
  slug: string
  title: string
  published_at?: string
  cover_image?: string
  content: string
  banner?: BlogBanner | null
  meta_title?: string
  meta_description?: string
  og_image?: string
}

const ArticleBanner: React.FC<{ banner: BlogBanner }> = ({ banner }) => {
  // Everything is optional. Background = uploaded image or legacy template.
  // Text overlay (title/description/button) renders only for fields that are
  // present. If `button_url` is set without a `button_text`, the whole banner
  // becomes a single clickable link instead of getting a visible button.
  const backgroundImage = banner.image || SkolaBannerSecondaryMobile
  const showButton = Boolean(banner.button_text && banner.button_url)
  const wholeClickable = !showButton && Boolean(banner.button_url)
  const hasTextOverlay = banner.title || banner.description

  const content = (
    <>
      <img
        src={backgroundImage}
        alt={banner.title ?? ''}
        className="block rounded-t-3xl w-full"
      />
      {hasTextOverlay && (
        <div className="grid grid-cols-12 gap-4 absolute top-5 left-8 pe-8">
          <div className="col-span-12 flex flex-col">
            {banner.title && (
              <h3 className="text-[25px] text-[#0183BA] mb-3 md:mb-2">{banner.title}</h3>
            )}
            {banner.description && (
              <div className="mb-6 leading-tight">
                <p className="mb-4 whitespace-pre-line">{banner.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
      {showButton && (
        <a
          href={banner.button_url!}
          className="cursor-pointer absolute bottom-[12%] left-1/2 -translate-x-1/2 text-white leading-tight text-[25px] bg-[#00A0E3] rounded-xl pt-3 py-2 px-4 transition-colors duration-200 no-underline hover:opacity-90 whitespace-nowrap"
        >
          {banner.button_text}
        </a>
      )}
    </>
  )

  return (
    <div className="px-4">
      <div className="container relative mx-auto">
        {wholeClickable ? (
          <a href={banner.button_url!} className="block">
            {content}
          </a>
        ) : (
          content
        )}
      </div>
    </div>
  )
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const { data: post, isLoading, isError } = useQuery(['blog-post', { slug }], getBlogPost)
  const p = post as BlogPostData | undefined

  usePageMeta({
    title: p && (p.meta_title || p.title),
    description: p && p.meta_description,
    image: p && p.og_image,
    type: 'article',
  })

  if (!p) {
    return null
  }

  return (
    <div className="px-4 md:px-0">
      <div className="container mx-auto mt-[52px] md:mt-[88px] py-12">
        {isLoading && <p className="text-gray-400">Načítám příspěvek…</p>}
        {isError && <p className="text-red-400">Nepodařilo se načíst příspěvek.</p>}
        <div className="grid grid-cols-12 gap-6">
          {/* Left: text content */}
          <div className="col-span-12 lg:col-span-8 flex flex-col">
            {p.published_at && (
              <div className="flex items-center gap-2 text-[#A8CD00] text-[16px] mb-2">
                <span className="shrink-0">{getReadableDate(p.published_at)}</span>
                <span className="flex-1 h-px bg-[#A8CD00]" />
              </div>
            )}
            <h1 className="text-[#0183BA] mb-3 text-xl md:text-[25px]">{p.title}</h1>
            {p.cover_image && (
              <img
                src={p.cover_image}
                alt={p.title}
                className="block lg:hidden w-full object-cover border border-gray-200 rounded-xl mb-6"
              />
            )}
            <div
              className="prose blog-post-content text-[#333333] text-[16px] flex-1"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(p.content) }}
            />
            <a href="/blog" className="text-[#0183BA] hover:underline mt-6 inline-block text-lg">
              <img src={backIcon} alt="Zpět na blog" className="inline-block mr-2" />
              Zpět
            </a>
          </div>

          {/* Right: cover image (desktop only) + admin-configurable CTA banner */}
          <div className="col-span-12 lg:col-span-4">
            {p.cover_image && (
              <img
                src={p.cover_image}
                alt={p.title}
                className="hidden lg:block w-full object-cover border border-gray-200 rounded-xl mb-6"
              />
            )}
            {p.banner && <ArticleBanner banner={p.banner} />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BlogPost
